import type {FC, ReactNode} from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import {
  useGetConversationQuery,
  useListMessagesQuery,
  useStopConversationRunMutation,
} from '@apis/aiCoreAPI.ts'
import type {Message} from '@interfaces/alpinist/message'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import {useConversationEvents} from '@containers/Dashboards/AgentChat/components/ConversationStreamProvider'
import {MESSAGE_PAGE_SIZE} from '@containers/Dashboards/AgentChat/components/chatMessageList/constants'
import type {StopMode} from '@interfaces/alpinist/ws'
import type {DeliveryLabel} from './runState'
import {
  deliveryLabel,
  emptyRunStore,
  isBusy,
  runReducer,
  strongerStop,
  viewOfMessage,
} from './runState'
import {takeWithdrawnTexts} from './withdrawnDraft'

export interface ConversationRun {
  /** Stop & steer is available: inside AgentChat's provider, not in the bare default. */
  enabled: boolean
  /** Stop has something to stop: a run works or is stopping, or a message waits. */
  busy: boolean
  /** Stop was asked for and the run has not ended. */
  stopping: boolean
  /** How: `soft` wraps up with an interim answer, `hard` cancels. Null when not stopping. */
  stopMode: StopMode | null
  /** A hard stop that is finishing a write first. */
  finishingWrite: boolean
  /** The running tool a stop waits for, as "POST signal-thresholds/" or the tool name. */
  waitingFor: string | null
  /** The last stop request failed. */
  stopFailed: boolean
  stop: () => void
  /** What a user message's bubble says about how it reached Alpinist, if anything. */
  deliveryOf: (message: Message) => DeliveryLabel | null
}

const OFF: ConversationRun = {
  enabled: false,
  busy: false,
  stopping: false,
  stopMode: null,
  finishingWrite: false,
  waitingFor: null,
  stopFailed: false,
  stop: () => undefined,
  deliveryOf: () => null,
}

const ConversationRunContext = createContext<ConversationRun>(OFF)

/** Stop & steer state of the open conversation; all off outside a ConversationRunProvider. */
export const useConversationRun = (): ConversationRun => useContext(ConversationRunContext)

const STOP_FAILED_NOTICE_MS = 6000

interface Props {
  conversationId: string
  /** Texts Stop withdrew before Alpinist read them, for the composer to take back. */
  onWithdrawnTexts: (texts: string[]) => void
  children: ReactNode
}

/**
 * Stop & steer for one conversation (AB#6760): whether a run holds it, Stop,
 * and how each message sent while it worked reached Alpinist.
 *
 * AgentChat always mounts it. A chat rendered without it (the bare default)
 * keeps the old rule: the composer locks from the first streamed event until
 * the answer.
 */
export const ConversationRunProvider: FC<Props> = ({
  conversationId,
  onWithdrawnTexts,
  children,
}) => {
  const [stored, dispatch] = useReducer(runReducer, conversationId, emptyRunStore)
  // A switch renders once before the reset lands; it must not show the
  // previous conversation's run meanwhile.
  const store = stored.conversationId === conversationId ? stored : emptyRunStore(conversationId)
  const storeRef = useRef(store)
  storeRef.current = store

  const [stopFailed, setStopFailed] = useState(false)

  useEffect(() => {
    dispatch({type: 'reset', conversationId})
    setStopFailed(false)
  }, [conversationId])

  // After a load, a reload or a reconnect the events were missed, so the run
  // state is read back from the API. Each read applies once, when it arrives:
  // `startedTimeStamp` is then the start of the request that returned it.
  const {
    currentData: conversation,
    startedTimeStamp: conversationRequestedAt,
    fulfilledTimeStamp: conversationReceivedAt,
    refetch: refetchConversation,
  } = useGetConversationQuery(conversationId, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  })
  const appliedConversationRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!conversation || !conversationRequestedAt || !conversationReceivedAt) return
    if (appliedConversationRef.current === conversationReceivedAt) return
    appliedConversationRef.current = conversationReceivedAt
    dispatch({
      type: 'conversationLoaded',
      conversationId,
      runActive: Boolean(conversation.run_active),
      stopping: Boolean(conversation.stopping),
      stopMode:
        conversation.stop_mode ??
        (typeof conversation.stopping === 'string' ? conversation.stopping : null),
      requestedAt: conversationRequestedAt,
    })
  }, [conversation, conversationId, conversationRequestedAt, conversationReceivedAt])

  // The newest page, where waiting messages are: the message list's own cache
  // entry, so this costs no request of its own.
  const {
    currentData: page,
    startedTimeStamp: pageRequestedAt,
    fulfilledTimeStamp: pageReceivedAt,
    refetch: refetchPage,
  } = useListMessagesQuery({conversationId, limit: MESSAGE_PAGE_SIZE, offset: 0})
  const appliedPageRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!page || !pageRequestedAt || !pageReceivedAt) return
    if (appliedPageRef.current === pageReceivedAt) return
    appliedPageRef.current = pageReceivedAt
    dispatch({
      type: 'messagesLoaded',
      conversationId,
      messages: page.results,
      requestedAt: pageRequestedAt,
    })
  }, [page, conversationId, pageRequestedAt, pageReceivedAt])

  // Who sent each message. A withdrawal reaches everyone viewing the
  // conversation, so only the sender's own texts go back into their draft.
  // Off without a UserProvider (the demo embed), where nothing else is shown.
  const currentUserId = (useUserContext() as UserContextProps | null)?.currentUser?.userprofile
    ?.identity_uuid
  const sendersRef = useRef(new Map<string, string>())
  const rememberSenders = (messages: readonly Message[]): void => {
    messages.forEach((message) => {
      if (message.role === 'user' && message.user_id) {
        sendersRef.current.set(message.id, String(message.user_id))
      }
    })
  }
  rememberSenders(page?.results ?? [])
  const isOwn = (id: string): boolean => {
    const sender = sendersRef.current.get(id)
    return !sender || !currentUserId || sender === String(currentUserId)
  }
  const isOwnRef = useRef(isOwn)
  isOwnRef.current = isOwn
  const rememberSendersRef = useRef(rememberSenders)
  rememberSendersRef.current = rememberSenders

  const {subscribe, connected} = useConversationEvents()
  const onWithdrawnRef = useRef(onWithdrawnTexts)
  onWithdrawnRef.current = onWithdrawnTexts

  useEffect(
    () =>
      subscribe({
        onMessage: (event) => {
          // The socket is re-pointed rather than torn down, so a frame for the
          // conversation just left can still land here.
          if (event.conversation_id && event.conversation_id !== conversationId) return
          // The one high-frequency event; it changes nothing once a run is known.
          if (event.kind === 'agent.token' && storeRef.current.status.active) return
          if (event.kind === 'msg.final') rememberSendersRef.current([event.payload])
          dispatch({type: 'event', conversationId, event, at: Date.now()})
          if (event.kind === 'message.withdrawn') {
            const {message_ids: ids, texts} = event.payload
            const own = ids.flatMap((id, index) =>
              isOwnRef.current(id) ? [{id, text: texts[index] ?? ''}] : [],
            )
            const handedBack = takeWithdrawnTexts({
              message_ids: own.map((entry) => entry.id),
              texts: own.map((entry) => entry.text),
            })
            if (handedBack.length) onWithdrawnRef.current(handedBack)
          }
        },
      }),
    [subscribe, conversationId],
  )

  // A reconnect may have missed events: read the state back. Not on the first
  // connect, which the mount's own reads already cover.
  const refetchRef = useRef<() => void>(() => undefined)
  refetchRef.current = (): void => {
    void refetchConversation()
    void refetchPage()
  }
  const connectionRef = useRef({conversationId, connected, everConnected: false})
  useEffect(() => {
    const previous = connectionRef.current
    const sameConversation = previous.conversationId === conversationId
    if (connected && sameConversation && !previous.connected && previous.everConnected) {
      refetchRef.current()
    }
    connectionRef.current = {
      conversationId,
      connected,
      everConnected: connected || (sameConversation && previous.everConnected),
    }
  }, [connected, conversationId])

  const [stopConversationRun] = useStopConversationRunMutation()
  // The first stop is soft: Alpinist wraps up and answers with what it has.
  // Stopping again while it does escalates to a hard stop, which cancels.
  const stop = useCallback(() => {
    const current = storeRef.current
    const stopping = strongerStop(current.status.stopMode, current.stopRequested)
    if (stopping === 'hard') return
    const mode: StopMode = stopping ? 'hard' : 'soft'
    setStopFailed(false)
    dispatch({type: 'stopRequested', conversationId, mode})
    const requestedAt = Date.now()
    stopConversationRun({conversation_id: conversationId, mode})
      .unwrap()
      .then((result) =>
        dispatch({
          type: 'stopAnswered',
          conversationId,
          state: result.state,
          mode: result.mode ?? mode,
          requestedAt,
        }),
      )
      .catch(() => {
        dispatch({type: 'stopFailed', conversationId})
        setStopFailed(true)
      })
  }, [conversationId, stopConversationRun])

  // Feedback on the last attempt, not a state to dismiss.
  useEffect(() => {
    if (!stopFailed) return
    const timer = setTimeout(() => setStopFailed(false), STOP_FAILED_NOTICE_MS)
    return () => clearTimeout(timer)
  }, [stopFailed])

  const runWorking = store.status.active || store.status.stopping
  const deliveryOf = useCallback(
    (message: Message): DeliveryLabel | null =>
      message.role === 'user'
        ? deliveryLabel(store.messages[message.id] ?? viewOfMessage(message), runWorking)
        : null,
    [store.messages, runWorking],
  )

  const value = useMemo<ConversationRun>(
    () => ({
      enabled: true,
      busy: isBusy(store),
      stopping: store.status.stopping || store.stopRequested !== null,
      stopMode: strongerStop(store.status.stopMode, store.stopRequested),
      finishingWrite:
        store.status.finishingWrite &&
        strongerStop(store.status.stopMode, store.stopRequested) === 'hard',
      waitingFor: store.waitingFor
        ? [store.waitingFor.method, store.waitingFor.path].filter(Boolean).join(' ') ||
          store.waitingFor.tool ||
          null
        : null,
      stopFailed,
      stop,
      deliveryOf,
    }),
    [store, stopFailed, stop, deliveryOf],
  )

  return <ConversationRunContext.Provider value={value}>{children}</ConversationRunContext.Provider>
}
